import React from 'react';
import { Link } from 'react-router-dom';
import useReveal from '../useReveal';
import canadaFlag from '../../../../images/canadaflag.png';
import './styles.scss';

function MapleLeafFlag({ className }) {
    return <img className={className} src={canadaFlag} alt="Canadian flag" />;
}

function Reveal({ as: Tag = 'div', className = '', delay = 0, children, ...rest }) {
    const [ref, revealed] = useReveal();
    const cls = `pl-about-reveal${revealed ? ' is-revealed' : ''}${className ? ` ${className}` : ''}`;
    return (
        <Tag
            ref={ref}
            className={cls}
            style={delay ? { transitionDelay: `${delay}ms` } : undefined}
            {...rest}
        >
            {children}
        </Tag>
    );
}

const whyCards = [
    {
        n: '01',
        title: 'Tools that actually fit a Canadian jobsite',
        body: "Procore and the legacy giants were built for US enterprise GCs. We built ProjectLoop on Canadian sites — for the trades, supers, and PMs who carry the work. CAD pricing, local data residency, and workflows that match how Canadian projects really run.",
    },
    {
        n: '02',
        title: "Pricing that doesn't punish you for winning",
        body: 'The construction software industry charges you a percentage of your construction volume. Build a bigger project, pay them more. We thought that was insane. Our pricing is flat, transparent, and published — no enterprise sales trap.',
    },
    {
        n: '03',
        title: 'Software that works at the slab edge',
        body: "The best feature is the one your foreman uses at 6:45 AM with one bar of LTE. Every screen is mobile-first, offline-ready, and fast on the cheapest tablet. You don't need to train your crew — they already know how to use it.",
    },
];

const valueCards = [
    {
        title: 'Honest by default',
        body: 'Public pricing. Month-to-month plans. Cancel anytime. No 47-page MSAs, no enterprise sales gauntlet, no surprise renewal hikes.',
        icon: (
            <svg viewBox="0 0 24 24" fill="none">
                <path d="M9 12L11 14L15 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="2" />
            </svg>
        ),
    },
    {
        title: 'Built for the field',
        body: 'Foreman-tested before it ships. Every workflow is designed to work with one hand, in a glove, on a tablet, in the rain.',
        icon: (
            <svg viewBox="0 0 24 24" fill="none">
                <rect x="6" y="2" width="12" height="20" rx="3" stroke="currentColor" strokeWidth="2" />
                <circle cx="12" cy="18" r="1" fill="currentColor" />
            </svg>
        ),
    },
    {
        title: 'Lean by design',
        body: 'No bloated sales floor. No overseas call centers. The savings of running a tight ship are passed straight to the customer — that\'s our motto.',
        icon: (
            <svg viewBox="0 0 24 24" fill="none">
                <path d="M3 12L7 8L12 13L21 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M21 4H15M21 4V10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        ),
    },
    {
        title: 'Canadian to the core',
        body: 'Our team, our customers, our compliance — all rooted in Canada. We answer the phone in your time zone. We know your codes.',
        icon: (
            <svg viewBox="0 0 24 24" fill="none">
                <path d="M12 2L14 8L20 9L15.5 13.5L17 20L12 16.5L7 20L8.5 13.5L4 9L10 8L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            </svg>
        ),
    },
];

const timeline = [
    {
        year: '2023',
        title: 'A frustration on a Toronto job site',
        body: "Two construction veterans and a Canadian software team kept hearing the same complaint: enterprise PM tools cost a fortune, take six weeks to onboard, and somehow still miss the basics. The idea for ProjectLoop took shape over coffee on a Tuesday.",
    },
    {
        year: '2024',
        title: 'Built in Ontario, by Ontario builders',
        body: 'Real foremen and real PMs sat in design reviews. We threw out the features they didn\'t use, sharpened the ones they couldn\'t live without, and shipped a private beta to a handful of GCs before charging anyone a dollar.',
    },
    {
        year: '2025',
        title: 'Public launch — flat, fair, and transparent',
        body: 'ProjectLoop went live with publicly listed CAD pricing — a deliberate departure from "call us for a quote" enterprise software. RFIs, drawings, daily logs, punch, schedule, forms — every workflow in one app, on every plan.',
    },
    {
        year: '2026',
        title: 'Coast-to-coast Canadian construction',
        body: 'From Ontario condos to Alberta industrial builds and BC infrastructure, Canadian GCs and trades adopted ProjectLoop as the alternative to per-volume vendor pricing. The next chapter: deeper integrations, tighter analytics, same flat fee.',
    },
];

export default function AboutUsPage() {
    return (
        <div className="pl-about">
            {/* ── Hero ─────────────────────────────────────────── */}
            <section className="pl-about__hero">
                <div className="pl-about__inner">
                    <div className="pl-about__hero-canada">
                        <MapleLeafFlag />
                        <span>Proudly Canadian</span>
                    </div>
                    <p className="pl-about__hero-eyebrow">About ProjectLoop</p>
                    <h1 className="pl-about__hero-title">
                        Construction software, <span>finally built for Canada.</span>
                    </h1>
                    <p className="pl-about__hero-lede">
                        We&apos;re a small, focused team of construction veterans and software builders on a
                        mission to give Canadian GCs and trades the same world-class project tools the
                        enterprise giants charge a fortune for — at a price that respects your margin.
                    </p>

                    <div className="pl-about__hero-stats">
                        <div className="pl-about__hero-stat">
                            <strong>$0</strong>
                            <span>Sales calls required</span>
                        </div>
                        <div className="pl-about__hero-stat">
                            <strong>90</strong>
                            <span>Days free trial</span>
                        </div>
                        <div className="pl-about__hero-stat">
                            <strong>100%</strong>
                            <span>Canadian-built</span>
                        </div>
                        <div className="pl-about__hero-stat">
                            <strong>1</strong>
                            <span>Flat monthly fee</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Mission ──────────────────────────────────────── */}
            <section className="pl-about__section">
                <Reveal className="pl-about__lead">
                    <p className="pl-about__eyebrow">Our mission</p>
                    <h2>Make first-class construction software accessible to every Canadian builder.</h2>
                </Reveal>

                <Reveal className="pl-about__mission">
                    <p className="pl-about__eyebrow">The promise</p>
                    <blockquote>
                        We will earn your business by being <em>better and cheaper</em> — not by locking
                        you into a 12-month contract or charging you a percentage of every dollar of work
                        you do.
                    </blockquote>
                </Reveal>
            </section>

            {/* ── Why we exist ─────────────────────────────────── */}
            <section className="pl-about__section pl-about__section--alt">
                <Reveal className="pl-about__lead">
                    <p className="pl-about__eyebrow">Why we built ProjectLoop</p>
                    <h2>The construction software industry needed an honest reset.</h2>
                    <p>
                        Big platforms got bigger, the bills got heavier, and somewhere along the way the
                        people doing the work stopped being the customer. We&apos;re here to flip that.
                    </p>
                </Reveal>

                <div className="pl-about__why-grid">
                    {whyCards.map((card, idx) => (
                        <Reveal key={card.title} className="pl-about__why-card" delay={idx * 100}>
                            <span className="pl-about__why-num">{card.n}</span>
                            <h3>{card.title}</h3>
                            <p>{card.body}</p>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* ── Story / timeline ─────────────────────────────── */}
            <section className="pl-about__section">
                <Reveal className="pl-about__lead">
                    <p className="pl-about__eyebrow">Our story</p>
                    <h2>From a Tuesday-morning complaint to a Canadian platform.</h2>
                    <p>
                        ProjectLoop didn&apos;t come out of a Silicon Valley pitch deck. It came out of
                        Canadian construction sites — and it&apos;s been shaped, every step of the way, by
                        the people who actually build.
                    </p>
                </Reveal>

                <div className="pl-about__timeline">
                    {timeline.map((item, idx) => (
                        <Reveal key={item.year} className="pl-about__timeline-row" delay={idx * 80}>
                            <span className="pl-about__timeline-dot" aria-hidden="true" />
                            <div className="pl-about__timeline-content">
                                <span className="pl-about__timeline-year">{item.year}</span>
                                <h3>{item.title}</h3>
                                <p>{item.body}</p>
                            </div>
                            <div className="pl-about__timeline-spacer" aria-hidden="true" />
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* ── Values (dark section) ────────────────────────── */}
            <section className="pl-about__section pl-about__section--dark">
                <Reveal className="pl-about__lead">
                    <p className="pl-about__eyebrow">What we stand for</p>
                    <h2>Four principles that drive every decision we make.</h2>
                    <p>
                        These aren&apos;t poster-on-the-wall values. They&apos;re the standard we hold every
                        feature, every email, and every invoice against.
                    </p>
                </Reveal>

                <div className="pl-about__values-grid">
                    {valueCards.map((v, idx) => (
                        <Reveal key={v.title} className="pl-about__value-card" delay={idx * 80}>
                            <span className="pl-about__value-icon">{v.icon}</span>
                            <h3>{v.title}</h3>
                            <p>{v.body}</p>
                        </Reveal>
                    ))}
                </div>
            </section>

            {/* ── Built by Canadians ──────────────────────────── */}
            <section className="pl-about__section pl-about__section--alt">
                <Reveal className="pl-about__team-card">
                    <div className="pl-about__team-flag">
                        <MapleLeafFlag />
                    </div>
                    <div>
                        <p className="pl-about__eyebrow">Built by Canadians, for Canadians</p>
                        <h3>A small team rooted in Ontario&apos;s construction industry.</h3>
                        <p>
                            ProjectLoop&apos;s founding team blends decades of hands-on Canadian construction
                            management with senior software engineering experience from across the country.
                            We&apos;ve managed the projects you&apos;re managing now — the ones with the tight
                            schedules, the picky owners, and the suprise change orders.
                        </p>
                        <p>
                            We&apos;re headquartered in Ontario, our data lives in Canadian-owned
                            infrastructure, and our support answers in your time zone. We know your codes,
                            your unions, your owners&apos; standards, and your weather.
                            <strong> When we say &ldquo;built for Canada,&rdquo; we mean it literally.</strong>
                        </p>
                    </div>
                </Reveal>
            </section>

            {/* ── Final CTA ───────────────────────────────────── */}
            <section className="pl-about__section">
                <Reveal className="pl-about__cta">
                    <p className="pl-about__eyebrow">Ready to see it run on your job?</p>
                    <h2>Try the whole platform free for 90 days.</h2>
                    <p>
                        No credit card. No demo gauntlet. No 12-month commitment. Run a real project with
                        your real crews — if ProjectLoop doesn&apos;t pay for itself by day 90, walk away.
                    </p>
                    <div className="pl-about__cta-actions">
                        <Link to="/signup" className="pl-about__btn pl-about__btn--primary">
                            Start free trial
                        </Link>
                        <Link to="/contactus" className="pl-about__btn pl-about__btn--ghost">
                            Talk to a builder
                        </Link>
                    </div>
                </Reveal>
            </section>
        </div>
    );
}
